import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocation, Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Users, Megaphone, DollarSign, CheckCircle, ArrowRight } from "lucide-react";
import logoUrl from "@assets/edufocal-logo-min-50292a59_1758905778671.png";

type ProgramTab = 'referral' | 'creator';

function ReferralPartnerForm() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    referralCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { confirmPassword, ...submitData } = formData;
      await apiRequest('POST', '/api/ref/signup', submitData);
      toast({
        title: "Account created!",
        description: "Welcome to the EduFocal Partner Program.",
      });
      setLocation('/ref/dashboard');
    } catch (error: any) {
      toast({
        title: "Sign up failed",
        description: error.message || "Unable to create account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            placeholder="Enter your first name"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            required
            data-testid="input-partner-first-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            placeholder="Enter your last name"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            required
            data-testid="input-partner-last-name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          required
          data-testid="input-partner-signup-email"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phoneNumber">Phone Number (Optional)</Label>
        <Input
          id="phoneNumber"
          type="tel"
          placeholder="Enter your phone number"
          value={formData.phoneNumber}
          onChange={(e) => handleChange('phoneNumber', e.target.value)}
          data-testid="input-partner-phone"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="referralCode">Referral Code</Label>
        <Input
          id="referralCode"
          placeholder="Choose a unique referral code"
          value={formData.referralCode}
          onChange={(e) => handleChange('referralCode', e.target.value)}
          required
          data-testid="input-partner-referral-code"
        />
        <p className="text-xs text-muted-foreground">
          This code will be used in your referral links (e.g., edufocal.com?ref=yourcode)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            required
            minLength={6}
            data-testid="input-partner-password"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Confirm your password"
            value={formData.confirmPassword}
            onChange={(e) => handleChange('confirmPassword', e.target.value)}
            required
            minLength={6}
            data-testid="input-partner-confirm-password"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full mt-4"
        size="lg"
        data-testid="button-partner-sign-up"
      >
        {isSubmitting ? "Creating account..." : "Create Partner Account"}
      </Button>
    </form>
  );
}

function CreatorApplicationForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    socialLink: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.socialLink) {
      toast({
        title: "Social link required",
        description: "Please provide your TikTok or Instagram profile link.",
        variant: "destructive",
      });
      return;
    }

    const socialUrl = formData.socialLink.toLowerCase().trim();
    const isValidSocialLink = 
      socialUrl.includes('tiktok.com/') || 
      socialUrl.includes('instagram.com/');
    if (!isValidSocialLink) {
      toast({
        title: "Invalid link",
        description: "Please paste a valid TikTok or Instagram profile link (e.g. https://tiktok.com/@yourname).",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await apiRequest('POST', '/api/creator/apply', formData);
      setSubmitted(true);
    } catch (error: any) {
      toast({
        title: "Submission failed",
        description: error.message || "Unable to submit application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 space-y-4">
        <div className="w-16 h-16 rounded-full bg-chart-2/10 flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-chart-2" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Application Received!</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          An email will be sent if approved. Thank you for your interest in the EduFocal Creator Program.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="creatorFirstName">First Name</Label>
          <Input
            id="creatorFirstName"
            placeholder="Enter your first name"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            required
            data-testid="input-creator-first-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="creatorLastName">Last Name</Label>
          <Input
            id="creatorLastName"
            placeholder="Enter your last name"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            required
            data-testid="input-creator-last-name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="creatorEmail">Email</Label>
        <Input
          id="creatorEmail"
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          required
          data-testid="input-creator-email"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="creatorPhone">Phone Number (Optional)</Label>
        <Input
          id="creatorPhone"
          type="tel"
          placeholder="Enter your phone number"
          value={formData.phoneNumber}
          onChange={(e) => handleChange('phoneNumber', e.target.value)}
          data-testid="input-creator-phone"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="socialLink">TikTok or Instagram Link</Label>
        <Input
          id="socialLink"
          type="url"
          placeholder="https://tiktok.com/@yourname or https://instagram.com/yourname"
          value={formData.socialLink}
          onChange={(e) => handleChange('socialLink', e.target.value)}
          required
          data-testid="input-creator-social-link"
        />
        <p className="text-xs text-muted-foreground">
          Paste the full link to your TikTok or Instagram profile (links only)
        </p>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full mt-4"
        size="lg"
        data-testid="button-creator-apply"
      >
        {isSubmitting ? "Submitting..." : "Submit Application"}
      </Button>
    </form>
  );
}

const referralBenefits = [
  { text: "$300 JMD per active referral monthly" },
  { text: "Instant account setup - start earning today" },
  { text: "Unique referral link & dashboard" },
  { text: "$2,000 JMD minimum cashout" },
];

const creatorBenefits = [
  { text: "$400 JMD per conversion from your content" },
  { text: "$1,000 JMD bonus per 10,000 views" },
  { text: "90-day attribution window" },
  { text: "$5,000 JMD minimum cashout" },
];

export default function PartnerSignup() {
  const [activeTab, setActiveTab] = useState<ProgramTab>('referral');
  const [, setLocation] = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/ref/me');
        if (response.ok) {
          setLocation('/ref/dashboard');
        }
      } catch (error) {
        // Not authenticated
      }
    };
    checkAuth();
  }, [setLocation]);

  const benefits = activeTab === 'referral' ? referralBenefits : creatorBenefits;

  return (
    <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary flex items-center justify-center p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-3xl mx-auto space-y-6">
        <div className="text-center space-y-3">
          <img src={logoUrl} alt="EduFocal" className="h-10 sm:h-12 w-auto mx-auto" data-testid="img-partner-signup-logo" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="text-partner-signup-headline">
            Partner with EduFocal
          </h1>
          <p className="text-white/80 text-sm sm:text-base max-w-lg mx-auto">
            Help Jamaican students prepare for PEP and earn money doing it. Choose the program that fits you best.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveTab('referral')}
            className={`flex items-center gap-2 justify-center p-3 sm:p-4 rounded-md border transition-colors ${
              activeTab === 'referral'
                ? 'bg-white text-foreground border-white shadow-lg'
                : 'bg-white/10 text-white border-white/20 hover-elevate'
            }`}
            data-testid="tab-referral-program"
          >
            <Users className="w-5 h-5 shrink-0" />
            <div className="text-left">
              <div className="font-semibold text-sm sm:text-base">Referral Partner</div>
              <div className={`text-xs ${activeTab === 'referral' ? 'text-muted-foreground' : 'text-white/60'}`}>
                Instant signup
              </div>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('creator')}
            className={`flex items-center gap-2 justify-center p-3 sm:p-4 rounded-md border transition-colors ${
              activeTab === 'creator'
                ? 'bg-white text-foreground border-white shadow-lg'
                : 'bg-white/10 text-white border-white/20 hover-elevate'
            }`}
            data-testid="tab-creator-program"
          >
            <Megaphone className="w-5 h-5 shrink-0" />
            <div className="text-left">
              <div className="font-semibold text-sm sm:text-base">Content Creator</div>
              <div className={`text-xs ${activeTab === 'creator' ? 'text-muted-foreground' : 'text-white/60'}`}>
                Application-based
              </div>
            </div>
          </button>
        </div>

        <Card className="w-full">
          <CardHeader className="pb-4 p-4 sm:p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-chart-2" />
                <h2 className="text-lg font-semibold text-foreground">
                  {activeTab === 'referral' ? 'Referral Partner Program' : 'Content Creator Program'}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {benefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-chart-2 mt-0.5 shrink-0" />
                    <span className="text-sm text-muted-foreground">{b.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            {activeTab === 'referral' ? <ReferralPartnerForm /> : <CreatorApplicationForm />}
          </CardContent>
        </Card>

        <div className="text-center text-sm text-white/70">
          Already have a partner account?{' '}
          <Link href="/ref/login">
            <span className="text-white font-medium underline underline-offset-2 cursor-pointer" data-testid="link-partner-login">
              Sign in here
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}
